import { styled } from '@porsche-code/kado-styled-system/jsx';
import {
  ConfirmationModal,
  DataTable,
  dateBetweenFilterFn,
  Skeleton,
  SkeletonWrapper,
  Spacer,
  Tooltip,
} from '@porsche-code/kado-ui';
import {
  PButtonPure,
  PInlineNotification,
  PText,
  useToastManager,
} from '@porsche-design-system/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { ClientError } from 'graphql-request';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { DateOutput, RouteGuard, StarsOutput } from '@/components';
import { NAMESPACES } from '@/config/i18n';
import { useAbilityContext } from '@/context';
import {
  useDeleteUserFeedbackMutation,
  UserFeedbacksQuery,
  useUserFeedbacksQuery,
} from '@/graphql';

export const Route = createFileRoute('/support/user-feedbacks')({
  component: () => (
    <RouteGuard can="read" a="UserFeedback">
      <UserFeedbacks />
    </RouteGuard>
  ),
  beforeLoad: () => {
    return {
      breadcrumb: () => <Trans i18nKey="userFeedback.headline" />,
    };
  },
});

const PAGE_SIZE = 10;

export const UserFeedbacks = () => {
  const { t } = useTranslation(NAMESPACES);
  const { state: ability } = useAbilityContext();

  const [error, setError] = useState<unknown>();

  const { isPending, data: queryData } = useUserFeedbacksQuery();

  const userFeedbacks = useMemo(
    () =>
      queryData?.userFeedbacks.map((userFeedback) => ({
        ...userFeedback,
      })),
    [queryData],
  );

  const totalUserFeedbacks = userFeedbacks?.length ?? 0;
  const averageRating = useMemo(() => {
    if (!userFeedbacks || userFeedbacks.length === 0) {
      return 0;
    }
    return (
      userFeedbacks.map((f) => f.rating).reduce((a, b) => a + b, 0) /
      userFeedbacks.length
    );
  }, [userFeedbacks]);

  const columns = useMemo(() => {
    const columnHelper =
      createColumnHelper<NonNullable<typeof userFeedbacks>[number]>();

    return [
      columnHelper.accessor('createdAt', {
        cell: (data) => {
          const createdAt = data.getValue();
          return createdAt ? <DateOutput date={createdAt} /> : 'n/a';
        },
        header: () => t('common:date'),
        size: 150,
        enableColumnFilter: true,
        enableSorting: true,
        filterFn: dateBetweenFilterFn,
        meta: {
          filterType: 'date',
        },
      }),
      columnHelper.accessor('comment', {
        cell: (data) => {
          const comment = data.getValue();

          return (
            <Tooltip
              withPortal
              placement="top-start"
              content={<ToolTipContent>{comment}</ToolTipContent>}
            >
              <PText ellipsis>{comment}</PText>
            </Tooltip>
          );
        },
        header: () => t('userFeedback.comment'),
        maxSize: 600,
        enableColumnFilter: true,
        enableSorting: false,
      }),
      columnHelper.accessor('rating', {
        cell: (data) => <StarsOutput rating={data.getValue()} />,
        header: () => t('userFeedback.rating'),
        size: 150,
        enableColumnFilter: true,
        enableSorting: true,
        filterFn: 'weakEquals',
        meta: {
          filterType: 'select',
          filterOptions: [1, 2, 3, 4, 5].map((star) => ({
            value: String(star),
            label:
              star === 1
                ? `1 ${t('userFeedback.star')}`
                : t('userFeedback.stars', { count: star }),
          })),
        },
      }),
      columnHelper.accessor('id', {
        cell: (data) => {
          const userFeedback = data.row.original;
          return (
            <TableCellActions>
              <DeleteButton
                userFeedbackId={userFeedback.id}
                onError={(err) => setError(err)}
                disabled={!ability.can('delete', 'UserFeedback')}
              />
            </TableCellActions>
          );
        },
        header: () => (
          <TableHeaderActions>{t('common:actions')}</TableHeaderActions>
        ),
        size: 40,
        enableColumnFilter: false,
        enableSorting: false,
      }),
    ];
  }, [ability, t]);

  return (
    <>
      {error && (
        <>
          <PInlineNotification
            heading={t('common:error')}
            state="error"
            onDismiss={() => setError(undefined)}
          >
            {error instanceof ClientError
              ? t((error.response.errors?.[0].message as any) ?? error.message)
              : `${error}`}
          </PInlineNotification>

          <Spacer mb="md" />
        </>
      )}

      <Spacer mb="md" />
      <Summary>
        {isPending ? (
          <SkeletonWrapper gap="sm" direction="column">
            <SkeletonWrapper>
              <Skeleton css={{ width: '120px' }} />
              <Skeleton css={{ width: '40px' }} />
            </SkeletonWrapper>
            <SkeletonWrapper>
              <Skeleton css={{ width: '120px' }} />
              <Skeleton css={{ width: '40px' }} />
            </SkeletonWrapper>
          </SkeletonWrapper>
        ) : (
          <>
            <PText>{t('userFeedback.totalCount')}:</PText>
            <PText weight="bold">{totalUserFeedbacks}</PText>
            <PText>{t('userFeedback.averageRating')}:</PText>
            <PText weight="bold">{averageRating.toFixed(2)}</PText>
          </>
        )}
      </Summary>

      <Spacer mb="md" />
      <DataTable
        caption={t('userFeedback.headline')}
        isLoading={isPending}
        data={userFeedbacks ?? []}
        pagination={{
          pageSize: PAGE_SIZE,
          position: 'bottom',
        }}
        columns={columns}
        idAccessor="id"
        i18n={{
          filterLabel: (columnName) =>
            t('common:iconLabel.filter', { columnName }),
          optionAll: t('common:all'),
          buttonReset: t('common:action.reset'),
          buttonFilter: t('common:action.filter'),
          actionSearch: t('common:action.search'),
          noData: t('common:noData'),
          minLabel: t('common:min'),
          maxLabel: t('common:max'),
        }}
      />
    </>
  );
};

const Summary = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    rowGap: 'sm',
    columnGap: 'md',
    py: 'md',
    px: 0,
  },
});

const ToolTipContent = styled('div', {
  base: {
    maxWidth: '700px',
    whiteSpace: 'pre-line',
    zIndex: 1000,
  },
});

const TableCellActions = styled('div', {
  base: {
    display: 'flex',
    justifyContent: 'end',
    gap: 'sm',
  },
});

const TableHeaderActions = styled('div', {
  base: {
    textAlign: 'right',
  },
});

const DeleteButton = ({
  userFeedbackId,
  disabled,
}: {
  userFeedbackId: string;
  disabled?: boolean;
  onError: (error: unknown) => void;
}) => {
  const { t } = useTranslation(NAMESPACES);
  const { addMessage } = useToastManager();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const { mutateAsync: deleteUserFeedback, isPending: isDeletingFeedback } =
    useDeleteUserFeedbackMutation({
      onSuccess: () => {
        queryClient.setQueryData<UserFeedbacksQuery>(
          useUserFeedbacksQuery.getKey(),
          (cache) =>
            cache && {
              ...cache,
              userFeedbacks: cache.userFeedbacks.filter(
                (userFeedback) => userFeedback.id !== userFeedbackId,
              ),
            },
        );
      },
    });

  return (
    <>
      <PButtonPure
        size="sm"
        icon="delete"
        onClick={() => setIsModalOpen(true)}
        disabled={disabled}
        hideLabel
      >
        {t('userFeedback.deleteUserFeedback')}
      </PButtonPure>
      <ConfirmationModal
        open={isModalOpen}
        description={t('userFeedback.confirmEffectsOfFeedbackDeletion')}
        heading={t('userFeedback.deleteUserFeedback')}
        onDismiss={() => setIsModalOpen(false)}
        i18n={{
          cancel: t('common:action.cancel'),
          confirm: t('common:action.delete'),
        }}
        isLoading={isDeletingFeedback}
        onConfirm={async () => {
          await deleteUserFeedback(
            { id: userFeedbackId },
            {
              onSettled: () => {
                setIsModalOpen(false);
                addMessage({
                  text: t('userFeedback.successfullyDeleted'),
                  state: 'success',
                });
              },
            },
          );
        }}
      />
    </>
  );
};
